import { useCallback, useSyncExternalStore } from 'react'
import {
  resolveTransitionTiming,
  type MotionPerformanceTier,
  type MotionSettings,
  type TransitionOverrides,
  type TransitionPresetName,
  type TransitionTiming,
} from '@/utils/transitions'

type Listener = () => void

const subscribers = new Set<Listener>()

const DEFAULT_SETTINGS: MotionSettings = {
  scale: 1,
  performance: 'medium',
  prefersReduced: false,
}

let currentSettings: MotionSettings = DEFAULT_SETTINGS
let listenersAttached = false

function detectPrefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  } catch {
    return false
  }
}

function detectPerformanceTier(): MotionPerformanceTier {
  if (typeof navigator === 'undefined') return 'medium'

  const cores = typeof navigator.hardwareConcurrency === 'number' ? navigator.hardwareConcurrency : 4
  const memory = typeof (navigator as Navigator & { deviceMemory?: number }).deviceMemory === 'number'
    ? (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 4
    : 4

  const score = cores + memory
  if (score <= 6) return 'low'
  if (score >= 14) return 'high'
  return 'medium'
}

function evaluateSettings(): MotionSettings {
  const prefersReduced = detectPrefersReducedMotion()
  const performance = detectPerformanceTier()

  let scale = 1
  if (performance === 'low') scale = 0.9
  else if (performance === 'high') scale = 1.05

  if (prefersReduced) {
    scale = Math.min(scale, 0.35)
  }

  return {
    scale,
    performance,
    prefersReduced,
  }
}

function applySettings(settings: MotionSettings) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  const effectiveScale = settings.prefersReduced ? Math.min(settings.scale, 0.35) : settings.scale
  root.style.setProperty('--motion-scale', effectiveScale.toString())
  root.dataset.motion = settings.prefersReduced ? 'reduced' : 'standard'
  root.dataset.motionPerformance = settings.performance
}

function notifySubscribers() {
  for (const listener of subscribers) {
    listener()
  }
}

function updateSettings() {
  const next = evaluateSettings()
  if (
    next.scale !== currentSettings.scale ||
    next.prefersReduced !== currentSettings.prefersReduced ||
    next.performance !== currentSettings.performance
  ) {
    currentSettings = next
    applySettings(currentSettings)
    notifySubscribers()
  }
}

function attachListeners() {
  if (listenersAttached || typeof window === 'undefined') return

  const media = window.matchMedia('(prefers-reduced-motion: reduce)')
  try {
    media.addEventListener('change', updateSettings)
  } catch {
    // Older browsers
    media.addListener(updateSettings)
  }

  window.addEventListener('focus', updateSettings)
  window.addEventListener('pageshow', updateSettings)
  listenersAttached = true
}

function subscribe(callback: Listener) {
  subscribers.add(callback)
  attachListeners()
  return () => {
    subscribers.delete(callback)
  }
}

function getSnapshot() {
  return currentSettings
}

// Initialise immediately if we are on the client
if (typeof document !== 'undefined') {
  currentSettings = evaluateSettings()
  applySettings(currentSettings)
  attachListeners()
}

export function useAdaptiveTransition() {
  const settings = useSyncExternalStore(subscribe, getSnapshot, () => DEFAULT_SETTINGS)

  const getTiming = useCallback(
    (preset: TransitionPresetName, overrides?: TransitionOverrides): TransitionTiming =>
      resolveTransitionTiming(preset, settings, overrides),
    [settings],
  )

  return {
    ...settings,
    getTiming,
  }
}

export type { TransitionPresetName, TransitionOverrides, TransitionTiming } from '@/utils/transitions'
